import {
  Box,
  Signature,
  SignatureRef,
  Typography,
} from '@branch-messenger/willow-ui'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'

import {
  AdvanceConfirmationFormSchema,
  advanceConfirmationSchema,
} from '@/containers/onDemandPay/TakeAdvance/lib/schemas/advance-confirmation-schema'
import { useTakeAdvance } from '@/containers/onDemandPay/TakeAdvance/TakeAdvanceContext'
import { centsToDollar } from '@/lib/currency'
import { formatISODateToDDMonYYYY } from '@/lib/date-helpers'
import { userQueryOptions } from '@/queries/auth'
import { onDemandPayCardQueryOptions } from '@/queries/card'
import { useRequestAdvance } from '@/queries/ewa'
import { useAdvanceQuote, useBranchVerification } from '@/selectors/ewa'
import { useOrg } from '@/selectors/organization'

import {
  AdvanceStepTitle,
  AmountConfirmationContainer,
  TakeAdvanceContentContainer,
  TakeAdvanceFooterButton,
} from '../../styles'
import { TakeAdvanceContent } from '../../TakeAdvanceContent'
import { TakeAdvanceFooter } from '../../TakeAdvanceFooter'
import { AdvanceConfirmationInfo } from './AdvanceConfirmationInfo'

export const AdvanceConfirmationContent = () => {
  const { nextStep, setTakeAdvanceValues, takeAdvanceValues } = useTakeAdvance()
  const signatureRef = useRef<SignatureRef>(null)

  const {
    formState: { isValid },
    control,
    handleSubmit,
  } = useForm<AdvanceConfirmationFormSchema>({
    resolver: yupResolver(advanceConfirmationSchema),
  })

  const {
    data: { appName },
  } = useOrg()

  const {
    data: { nextPayDay },
  } = useBranchVerification()

  const {
    data: { first_name, last_name },
  } = useSuspenseQuery(userQueryOptions)

  const { data: onDemandPayCard } = useSuspenseQuery(
    onDemandPayCardQueryOptions()
  )

  const { mutate: requestAdvance, isPending: requestingAdvance } =
    useRequestAdvance()

  const destination = `${onDemandPayCard?.network} debit card ending in ${onDemandPayCard?.last4} for ${appName}`
  const isDisabled = !isValid || requestingAdvance
  const advanceAmount = takeAdvanceValues.advance_amount || '0'

  const { data: advanceQuote } = useAdvanceQuote(advanceAmount)

  const handleRequestAdvance = async () => {
    if (!advanceQuote) return
    const { quoteAmountRaw, feeAmountRaw, advanceQuote: quote } = advanceQuote
    const signatureData = await signatureRef?.current?.downloadSignature()
    requestAdvance(
      {
        amount: quoteAmountRaw,
        fee: feeAmountRaw,
        signature: signatureData ?? '',
        advance_quote: quote,
        payout_type:
          takeAdvanceValues.card_type === 'EWA_CARD'
            ? 'BRANCH_DIRECT'
            : 'WALLET',
      },
      {
        onSettled: data => {
          if (!data) {
            close()
            return
          }
          setTakeAdvanceValues({
            ...takeAdvanceValues,
            transfer_id: data.transfer_id,
            status: data.status,
          })
          nextStep()
        },
      }
    )
  }

  return (
    <TakeAdvanceContentContainer
      as="form"
      onSubmit={handleSubmit(handleRequestAdvance)}
    >
      <TakeAdvanceContent>
        <AmountConfirmationContainer>
          <AdvanceStepTitle>{`Confirm Advance`}</AdvanceStepTitle>
          <Typography $size="sm">{`Advance amount will be transferred to your ${destination} within minutes.`}</Typography>
          <Box $display="flex" $direction="column" $gap={6}>
            <AdvanceConfirmationInfo
              title={`Today's Advance`}
              value={centsToDollar(+advanceAmount * 100)}
            />
            <AdvanceConfirmationInfo
              title={`Transfer Fee`}
              value={advanceQuote?.feeAmount ?? ''}
            />
            <AdvanceConfirmationInfo
              title={`Amount Received`}
              value={advanceQuote?.receivedAmount ?? ''}
              valueDescription={`(Today's advance minus transfer fee)`}
            />
            <AdvanceConfirmationInfo
              title={`Deduction Info`}
              value={`${formatISODateToDDMonYYYY(nextPayDay)} paycheck`}
            />
          </Box>
          <Controller
            control={control}
            name="signature"
            render={({ field }) => (
              <Signature
                firstName={first_name}
                lastName={last_name}
                //TODO: Add link for `on demand pay terms`
                disclaimer={`By requesting an advance payment, you authorize your employer or Branch, as applicable, to deduct the amount advanced from your future paychecks or Branch Account, as applicable, until it has been fully repaid in accordance with Branch's On-Demand Pay Terms.`}
                {...field}
                ref={signatureRef}
              />
            )}
          />
        </AmountConfirmationContainer>
      </TakeAdvanceContent>
      <TakeAdvanceFooter>
        <TakeAdvanceFooterButton
          type="submit"
          loading={requestingAdvance}
          disabled={isDisabled}
        >
          Continue
        </TakeAdvanceFooterButton>
      </TakeAdvanceFooter>
    </TakeAdvanceContentContainer>
  )
}
